import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Stack } from '@mui/material'
import React from 'react'
import { useWatch } from 'react-hook-form'
import { useIsWebviewMode } from 'src/Hooks'
import useModals from 'src/Hooks/useModals'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { checkTrueRecursively } from 'src/Libs/flow/utils'
import { makeStyles } from 'tss-react/mui'
import { MUI_BOTTOM_BAR_HEIGHT, installationFormHeaderHeightMedium } from '../../constants'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'

const useStyles = makeStyles()(theme => ({
  buttonBox: {
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      left: 0,
      right: 0,
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
      transition: 'transform 0.2s ease',
    },
    [theme.breakpoints.up('sm')]: {
      position: 'sticky',
      height: installationFormHeaderHeightMedium,
    },
  },
  buttonStack: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',

      '& button': {
        width: '100%',
      },
    },
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-end',
      alignItems: 'center',
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2),
    },
  },
}))
//#region Main Component

/**
 * Header for the installation form, including action buttons.
 */
export const InstallationFormFooter: React.FC = () => {
  const { t } = useCustomTranslation()

  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { openConfirmationModal } = useModals()

  const methods = useInstallationForm()
  const {
    control,
    formState: { dirtyFields, errors },
    reset,
    trigger,
  } = methods

  // exclude gpsLatitude, gpsLongitude from dirty state
  // they will be saved if location is dirty
  const {
    gpsLatitude: _gpsLatitude,
    gpsLongitude: _gpsLongitude,
    chargingStationsExpandedState: _chargingStationsExpandedState,
    ...otherDirtyFields
  } = dirtyFields ?? {}
  const isDirty = checkTrueRecursively(otherDirtyFields)
  const isValid = Object.keys(errors).length === 0

  const isNewInstallation = useWatch({ control, name: 'id' }) === null

  const enableSave = (isNewInstallation || isDirty) && isValid

  const handleReset = (): void => {
    openConfirmationModal({
      title: t('installation.cancel.title'),
      text: t('installation.cancel.text'),
      buttonText: [t('installation.cancel.cancel'), t('installation.cancel.confirm')],
      onConfirm: (onClose): void => {
        reset()

        trigger()

        onClose()
      },
    })
  }

  return <ActionButtons handleReset={handleReset} isDirty={isDirty} enableSave={enableSave} isValid={isValid} canEdit={canEdit} />
}
//#endregion

//#region ActionButton
interface ActionButtonProps {
  handleReset: () => void
  isDirty: boolean
  enableSave: boolean
  isValid: boolean
  canEdit: boolean
}

const ActionButtons: React.FC<ActionButtonProps> = ({ handleReset, isDirty, enableSave, isValid, canEdit }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const isWebView = useIsWebviewMode()

  const isDisabled = !enableSave || !canEdit

  return (
    <Box className={classes.buttonBox} sx={{ zIndex: 'fab', bottom: [isWebView ? MUI_BOTTOM_BAR_HEIGHT : 0, 2, 1] }}>
      <Stack className={classes.buttonStack}>
        <Button type="button" variant="outlined" size="large" color="secondary" onClick={handleReset} disabled={!isDirty || !canEdit}>
          {t('common.cancel')}
        </Button>
        <OptionalTooltipWrapper hasTooltip={isDisabled} placement="top" titleKey="installation.common.cannot-save-because-error">
          <Button type="submit" variant="contained" size="large" color="secondary" disabled={isDisabled}>
            {t('common.save')}
          </Button>
        </OptionalTooltipWrapper>
      </Stack>
    </Box>
  )
}
//#endregion
